import type { Question } from "./types";

/**
 * Normalize a free-text answer for lenient comparison:
 * lowercase, trim, collapse internal whitespace, strip accents/diacritics,
 * and drop surrounding punctuation. (Client decision: lenient grading.)
 */
export function normalizeAnswer(input: string): string {
  return input
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // strip diacritics
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, " ") // punctuation -> space
    .replace(/\s+/g, " ")
    .trim();
}

/** True if a typed answer matches any accepted answer (lenient). */
export function isTextAnswerCorrect(
  typed: string | undefined,
  acceptedAnswers: string[] | undefined,
): boolean {
  if (!typed || !acceptedAnswers || acceptedAnswers.length === 0) return false;
  const t = normalizeAnswer(typed);
  if (!t) return false;
  return acceptedAnswers.some((a) => normalizeAnswer(a) === t);
}

/** True if the selected choice id is the correct one. */
export function isChoiceAnswerCorrect(
  choiceId: string | undefined,
  question: Question,
): boolean {
  if (!choiceId || !question.choices) return false;
  const choice = question.choices.find((c) => c.id === choiceId);
  return !!choice?.correct;
}

/** Fisher–Yates shuffle (returns a new array). */
export function shuffle<T>(arr: readonly T[]): T[] {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/** Pick up to `count` random items. */
export function pickRandom<T>(arr: readonly T[], count: number): T[] {
  return shuffle(arr).slice(0, count);
}
